import { keyframes, styled } from "goober";
import { useState, type ReactNode } from "react";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const alternateSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled("span")`
  display: inline-flex;
  &[data-spinning="true"] {
    animation: ${spin} 0.7s linear infinite;
  }
  &[data-turning="true"][data-turn-phase="first"] {
    animation: ${spin} 0.7s linear;
  }
  &[data-turning="true"][data-turn-phase="alternate"] {
    animation: ${alternateSpin} 0.7s linear;
  }
`;

export function RefreshSpin({
  spinning,
  turnKey,
  resting,
  children,
}: {
  spinning: boolean;
  turnKey?: number;
  resting?: ReactNode;
  children: ReactNode;
}) {
  const [animating, setAnimating] = useState(spinning);
  const [turnState, setTurnState] = useState({
    previousTurnKey: turnKey,
    remaining: 0,
    alternate: false,
  });

  if (spinning && !animating) setAnimating(true);
  if (turnKey !== turnState.previousTurnKey) {
    const addedTurns =
      turnKey !== undefined &&
      turnState.previousTurnKey !== undefined &&
      turnKey > turnState.previousTurnKey
        ? turnKey - turnState.previousTurnKey
        : 1;
    setTurnState({
      ...turnState,
      previousTurnKey: turnKey,
      remaining: turnState.remaining + addedTurns,
    });
  }

  const turning = turnState.remaining > 0;
  const active = animating || turning;

  return (
    <Container
      data-spinning={animating}
      data-turning={!animating && turning}
      data-turn-phase={turnState.alternate ? "alternate" : "first"}
      onAnimationIteration={(event) => {
        if (event.target === event.currentTarget && !spinning) {
          setAnimating(false);
        }
      }}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) return;
        setTurnState((state) => ({
          ...state,
          remaining: Math.max(0, state.remaining - 1),
          alternate: state.remaining > 1 ? !state.alternate : state.alternate,
        }));
      }}
    >
      {active ? children : (resting ?? children)}
    </Container>
  );
}
